import { isNonProdEnvironment } from '@sitecore-ui/portal-singular';
import {
  Destination,
  DestinationCdpp,
  DestinationSearch,
  IConfigUnifiedTrackingResponse,
  ISiteUnifiedTracking,
} from 'features/unified-tracking/services/unified-tracking/unifiedTrackingModel';

export const getCustomer = (
  id: string,
  nodes: Array<{
    __typename?: 'ContextUserApplication';
    id: string;
    name: string;
    displayName: string;
    organizationId: string;
    state: string;
    systemId: string;
    productCode: string;
    labels: Array<{
      __typename?: 'KeyValuePairOfStringAndString';
      key: string;
      value: string;
    }>;
    annotations?: Array<{
      __typename?: 'KeyValuePairOfStringAndString';
      key: string;
      value: string;
    }>;
    appearance?: {
      __typename?: 'UserApplicationAppearance';
      web: {
        __typename?: 'WebUserApplicationAppearance';
        actions?: {
          __typename?: 'ActionsConnection';
          nodes?: Array<{
            __typename?: 'Action';
            name: string;
            color: string;
            displayName?: string | null;
            location?: Array<string> | null;
            category?: string | null;
            description?: string | null;
            link: { __typename?: 'Link'; type: string; to: string };
            icon: { __typename?: 'Icon'; src: string; type: string };
          }> | null;
        } | null;
      };
    } | null;
  }> | null,
) => nodes?.find((node) => node.id === id);

export const getTenantClientKey = (tenant: any) =>
  tenant?.labels?.find((label: any) => label.key === 'clientKey')?.value;

export const getTenantAnotation = (tenant: any, key: string) =>
  tenant?.annotations?.find((label: any) => label.key === key)?.value;

export const getCustomerEnvironmentType = (
  id: string,
  nodes: Array<{
    __typename?: 'ContextUserApplication';
    id: string;
    name: string;
    displayName: string;
    organizationId: string;
    state: string;
    systemId: string;
    productCode: string;
    labels: Array<{
      __typename?: 'KeyValuePairOfStringAndString';
      key: string;
      value: string;
    }>;
    annotations?: Array<{
      __typename?: 'KeyValuePairOfStringAndString';
      key: string;
      value: string;
    }>;
    appearance?: {
      __typename?: 'UserApplicationAppearance';
      web: {
        __typename?: 'WebUserApplicationAppearance';
        actions?: {
          __typename?: 'ActionsConnection';
          nodes?: Array<{
            __typename?: 'Action';
            name: string;
            color: string;
            displayName?: string | null;
            location?: Array<string> | null;
            category?: string | null;
            description?: string | null;
            link: { __typename?: 'Link'; type: string; to: string };
            icon: { __typename?: 'Icon'; src: string; type: string };
          }> | null;
        } | null;
      };
    } | null;
  }> | null,
): string =>
  nodes
    ?.filter((node) => node.id === id)[0]
    ?.labels?.filter(
      (label) =>
        label.key === 'CustomerEnvironmentType' &&
        !isNonProdEnvironment(label.value),
    )[0]?.value ?? '';

export const getTenantSitesAndDestinations = (
  tenantData: Map<string, IConfigUnifiedTrackingResponse>,
) => {
  const sitesUnique: ISiteUnifiedTracking[] = [];
  const destinationUnique = new Map<string, Destination>();

  tenantData?.forEach((tenant) =>
    tenant.dataFlows?.forEach((dataFlow) => {
      dataFlow.sites?.forEach((site) => sitesUnique.push(site));
      dataFlow.destinations?.forEach((destination) => {
        switch (destination.id) {
          case 'cdpp':
            destinationUnique.set(
              (destination as DestinationCdpp).clientKey,
              destination,
            );
            break;

          case 'search':
            destinationUnique.set(
              (destination as DestinationSearch).secrets.domainId,
              destination,
            );
            break;

          default:
            destinationUnique.set(destination.id, destination);
            break;
        }
      });
    }),
  );

  return {
    sitesUnique: orderSites(sitesUnique),
    destinationUnique,
  };
};

export const getRelatedDestinations = (
  tenantData: Map<string, IConfigUnifiedTrackingResponse>,
  site: ISiteUnifiedTracking,
  destinations: {
    tenant: any;
    displayName: any;
    name: any;
    id: string;
    icon: any;
    customerEnvironmentType: string;
  }[],
  apps: {
    tenant:
      | {
          __typename?: 'ContextUserApplication';
          id: string;
          name: string;
          displayName: string;
          organizationId: string;
          state: string;
          systemId: string;
          productCode: string;
          labels: Array<{
            __typename?: 'KeyValuePairOfStringAndString';
            key: string;
            value: string;
          }>;
          annotations?: Array<{
            __typename?: 'KeyValuePairOfStringAndString';
            key: string;
            value: string;
          }>;
          appearance?: {
            __typename?: 'UserApplicationAppearance';
            web: {
              __typename?: 'WebUserApplicationAppearance';
              actions?: {
                __typename?: 'ActionsConnection';
                nodes?: Array<{
                  __typename?: 'Action';
                  name: string;
                  color: string;
                  displayName?: string | null;
                  location?: Array<string> | null;
                  category?: string | null;
                  description?: string | null;
                  link: { __typename?: 'Link'; type: string; to: string };
                  icon: { __typename?: 'Icon'; src: string; type: string };
                }> | null;
              } | null;
            };
          } | null;
        }
      | undefined;
    displayName: any;
    name: any;
    id: string;
    icon: any;
    customerEnvironmentType: string;
  }[],
  index: number | undefined,
) => {
  tenantData?.forEach((tenant) =>
    tenant.dataFlows?.forEach((dataFlow) => {
      if (dataFlow.sites?.some((siteDf) => siteDf.id === site.id)) {
        destinations.push(
          ...apps.filter((app) =>
            dataFlow.destinations?.some((dest) => {
              switch (dest.id) {
                case 'cdpp':
                  return getTenantClientKey(app.tenant) === dest.clientKey;

                case 'search':
                  return (
                    dest.secrets?.domainId ===
                    getTenantAnotation(app.tenant, 'TenantClaims.domainId')
                  );
                  break;
              }
            }),
          ),
        );
      }
    }),
  );

  const lastDestIndex = apps.findIndex(
    (elem) => elem.id === destinations[destinations.length - 1]?.id,
  );

  const selectedSiteRelations = {
    site,
    index: index,
    destinations,
    lastDestIndex,
  };
  return selectedSiteRelations;
};

export const getRelatedSites = (
  destination: {
    tenant: any;
    displayName: string;
    name: string;
    id: string;
    icon: string;
    customerEnvironmentType: string;
  },
  tenantData: Map<string, IConfigUnifiedTrackingResponse>,
  sites: ISiteUnifiedTracking[],
  index: number | undefined,
) => {
  const sitesFound: ISiteUnifiedTracking[] = [];

  switch (destination.tenant?.productCode) {
    case 'CDP':
    case 'Personalize':
      // eslint-disable-next-line no-case-declarations
      const destCk = getTenantClientKey(destination.tenant);

      tenantData?.forEach((tenant) =>
        tenant.dataFlows?.forEach((dataFlow) => {
          const hasDest = dataFlow.destinations?.some(
            (dest) => dest.id === 'cdpp' && dest.clientKey === destCk,
          );

          if (hasDest) {
            sitesFound.push(...dataFlow.sites);
          }
        }),
      );

      break;

    case 'Search':
      tenantData?.forEach((tenant) =>
        tenant.dataFlows?.forEach((dataFlow) => {
          const hasDest = dataFlow.destinations?.some(
            (dest) =>
              dest.id === 'search' &&
              dest.secrets?.domainId ===
                getTenantAnotation(destination.tenant, 'TenantClaims.domainId'),
          );

          if (hasDest) {
            sitesFound.push(...dataFlow.sites);
          }
        }),
      );

      break;
  }

  const orderedSites = orderSites(sitesFound);
  const lastSiteIndex = sites.findIndex(
    (elem) => elem.id === orderedSites[orderedSites.length - 1]?.id,
  );

  const selectedSiteRelations = {
    destination,
    index,
    sites: orderedSites,
    lastSiteIndex,
  };
  return selectedSiteRelations;
};

const orderSites = (sites: ISiteUnifiedTracking[]) =>
  sites.sort((a, b) => a.id.localeCompare(b.id));
